import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandS, BrandXXL } from '@sorare/core/src/atoms/typography';
import { BackButton } from '@sorare/core/src/components/dialog/BackButton';
import { HideSeriesBottomNav } from '@sorare/core/src/components/navigation/AppNavigation/HideSeriesBottomNav';
import {
  ANY_SPORT_SERIES,
  ANY_SPORT_SERIES_SQUAD_CREATE_OR_JOIN,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSafePreviousNavigate } from '@sorare/core/src/hooks/useSafePreviousNavigate';

import squad from '#src/assets/squad.webp';
import { SquadBackground } from '#src/components/squad/SquadBackground/index.tsx';
import { SquadCorners } from '#src/components/squad/SquadCorners/index.tsx';

import cardPackLeft from './assets/card-pack-left.webp';
import cardPackRight from './assets/card-pack-right.webp';
import field from './assets/field.webp';

const Root = styled(Vertical)`
  position: relative;
  min-height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
  align-items: center;
`;

const BackButtonWrapper = styled.div`
  position: absolute;
  top: var(--triple-unit);
  left: var(--double-unit);
  z-index: 2;
`;

const SquadCornersWrapper = styled.div`
  position: absolute;
  top: calc(5 * var(--unit));
  left: 0;
  right: 0;
  bottom: 0;
`;

const Content = styled(Vertical)`
  position: relative;
  z-index: 1;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: var(--quadruple-unit);
  width: 100%;
`;

const TitleWrapper = styled(Horizontal)`
  align-items: center;
  justify-content: center;
`;

const Medal = styled.img`
  width: 48px;
  height: auto;
`;

const CardPacksArea = styled.div`
  position: relative;
  width: 100%;
  max-width: 350px;
  aspect-ratio: 350 / 260;
`;

const Field = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
`;

const CardPack = styled.img`
  position: absolute;
  height: 85%;
  width: auto;
  top: 0;
  left: 50%;

  &.left {
    transform: translateX(-70%) rotate(-15deg);
  }
  &.right {
    transform: translateX(-30%) rotate(15deg);
  }
`;

const TITLE_GRADIENT = 'linear-gradient(180deg, #aa83ff 18%, #7d41ff 100%)';

export const SquadWelcomeDialog = () => {
  const { generateSportContextPath } = useSportContext();
  const safeBack = useSafePreviousNavigate(
    generateSportContextPath(ANY_SPORT_SERIES)
  );

  return (
    <Root>
      <SquadBackground />
      <SquadCornersWrapper>
        <SquadCorners />
      </SquadCornersWrapper>
      <BackButtonWrapper>
        <BackButton onBack={safeBack} color="quaternary" />
      </BackButtonWrapper>
      <Content gap={3}>
        <Horizontal gap={0.5} center>
          <SorareLogo size="small" />
          <BrandS>
            <FormattedMessage defaultMessage="Assemble Squad" />
          </BrandS>
        </Horizontal>
        <Vertical gap={1} center>
          <TitleWrapper gap={0.5}>
            <Medal src={squad} alt="" />
            <BrandXXL
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': TITLE_GRADIENT,
                } as CSSProperties
              }
            >
              <FormattedMessage defaultMessage="Squads" />
            </BrandXXL>
          </TitleWrapper>
          <BodyL color="var(--c-white)" className="text-center">
            <FormattedMessage defaultMessage="Earn exclusive packs by playing with your friends" />
          </BodyL>
        </Vertical>
        <CardPacksArea>
          <Field src={field} alt="" />
          <CardPack
            className="left"
            src={cardPackLeft}
            alt=""
            style={{ zIndex: 2 }}
          />
          <CardPack className="right" src={cardPackRight} alt="" />
        </CardPacksArea>
        <Button
          size="large"
          color="primary"
          to={generateSportContextPath(ANY_SPORT_SERIES_SQUAD_CREATE_OR_JOIN)}
        >
          <FormattedMessage defaultMessage="Let's go!" />
        </Button>
      </Content>
      <HideSeriesBottomNav />
    </Root>
  );
};
